import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Crown, Trophy, Sparkle } from "@phosphor-icons/react";
import client from "../lib/api";
import { Button } from "./ui/button";

const TIER_SUB = {
  Silver: "Steady momentum — you're building a real book of business.",
  Gold: "You're among Reelty's most active agents. Keep it rolling.",
  Platinum: "Elite company. Your listings move, and it shows.",
  Elite: "The very top tier. You set the standard on Reelty.",
};
const SHARD_COLORS = ["#E9C77E", "#B08D57", "#FBFAF7", "#C9A25A"];

export default function LevelUpCelebration() {
  const [data, setData] = useState(null);
  const reduce = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    client.get("/rewards/level-up").then((r) => { if (r.data?.level_up) setData(r.data); }).catch(() => {});
  }, []);

  const shards = useMemo(() => (reduce || !data ? [] : Array.from({ length: 28 }).map((_, i) => ({
    key: i,
    x: (Math.random() * 2 - 1) * 520,
    y: 260 + Math.random() * 340,
    rot: Math.random() * 720 - 360,
    delay: Math.random() * 0.35,
    dur: 1.6 + Math.random() * 1.1,
    color: SHARD_COLORS[i % SHARD_COLORS.length],
    w: 6 + Math.random() * 8,
  })) : []), [data, reduce]);

  const close = () => setData(null);
  const isTop = data?.tier === "Elite" || data?.tier === "Platinum";

  return (
    <AnimatePresence>
      {data && (
        <motion.div key="levelup" className="fixed inset-0 z-[100] flex items-center justify-center p-6"
          data-testid="level-up-celebration"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}>
          <div className="absolute inset-0 bg-[#2A0A0A]/85 backdrop-blur-md" onClick={close} />
          <div className="absolute inset-0" style={{ background: "radial-gradient(60% 50% at 50% 38%, rgba(233,199,126,0.22), transparent 70%)" }} />

          {shards.map((s) => (
            <motion.div key={s.key} className="absolute left-1/2 top-[34%] pointer-events-none"
              initial={{ x: 0, y: 0, opacity: 1, rotate: 0 }}
              animate={{ x: s.x, y: s.y, opacity: 0, rotate: s.rot }}
              transition={{ duration: s.dur, delay: s.delay, ease: "easeOut" }}
              style={{ width: s.w, height: s.w * 0.5, background: s.color, borderRadius: 1 }} />
          ))}

          <motion.div className="relative text-center max-w-md w-full"
            initial={{ scale: 0.82, y: 24, opacity: 0 }} animate={{ scale: 1, y: 0, opacity: 1 }}
            transition={{ type: "spring", stiffness: 220, damping: 18, delay: 0.05 }}>
            <div className="relative mx-auto w-28 h-28 mb-6">
              <motion.div className="absolute inset-0 rounded-full" style={{ background: "radial-gradient(circle, rgba(233,199,126,0.45), transparent 68%)" }}
                animate={reduce ? {} : { scale: [1, 1.12, 1], opacity: [0.7, 1, 0.7] }} transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }} />
              <div className="absolute inset-2 rounded-full flex items-center justify-center ring-1 ring-[#E9C77E]/50 shadow-[0_10px_40px_rgba(233,199,126,0.35)]"
                style={{ background: "linear-gradient(145deg, #E9C77E, #B08D57)" }}>
                {isTop ? <Crown size={48} weight="fill" className="text-[#1A1715]" /> : <Trophy size={46} weight="fill" className="text-[#1A1715]" />}
              </div>
            </div>

            <p className="text-[11px] font-semibold tracking-[0.28em] uppercase text-[#E9C77E]/80 flex items-center justify-center gap-1.5">
              <Sparkle size={13} weight="fill" />You've leveled up
            </p>
            <h2 className="font-display text-5xl sm:text-6xl text-[#FBFAF7] leading-none mt-2" data-testid="level-up-tier">{data.tier}</h2>
            <p className="text-sm text-[#FBFAF7]/70 mt-4 leading-relaxed">
              You're now a <span className="text-[#E9C77E] font-semibold">{data.tier}</span> member of Reelty Rewards.<br />
              {TIER_SUB[data.tier] || "Keep booking to unlock even more perks."}
            </p>
            {data.points != null && (
              <p className="text-xs text-[#FBFAF7]/45 mt-3 font-mono tabular-nums">
                {Number(data.points).toLocaleString()} points{data.cash_value != null ? ` · worth $${Number(data.cash_value).toFixed(2)}` : ""}
              </p>
            )}
            <Button data-testid="level-up-continue" onClick={close}
              className="mt-7 bg-[#E9C77E] hover:bg-[#dcb968] text-[#1A1715] font-semibold px-8">
              Continue
            </Button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
